'use client';
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import * as React from 'react';
import { unstable_useId as useId, unstable_useForkRef as useForkRef } from '@mui/utils';
import { useTabsContext } from "../Tabs/index.js";
import { extractEventHandlers } from "../utils/extractEventHandlers.js";
import { useCompoundItem } from "../useCompound/index.js";
import { useListItem } from "../useList/index.js";
import { useButton } from "../useButton/index.js";
import { combineHooksSlotProps } from "../utils/combineHooksSlotProps.js";
function tabValueGenerator(otherTabValues) {
    return otherTabValues.size;
}
/**
 *
 * Demos:
 *
 * - [Tabs](https://mui.com/base-ui/react-tabs/#hooks)
 *
 * API:
 *
 * - [useTab API](https://mui.com/base-ui/react-tabs/hooks-api/#use-tab)
 */ function useTab(parameters) {
    var valueParam = parameters.value, externalRef = parameters.rootRef, _parameters_disabled = parameters.disabled, disabled = _parameters_disabled === void 0 ? false : _parameters_disabled, idParam = parameters.id;
    var tabRef = React.useRef(null);
    var id = useId(idParam);
    var _useTabsContext = useTabsContext(), selectedValue = _useTabsContext.value, selectionFollowsFocus = _useTabsContext.selectionFollowsFocus, getTabPanelId = _useTabsContext.getTabPanelId;
    var tabMetadata = React.useMemo(function() {
        return {
            disabled: disabled,
            ref: tabRef,
            id: id
        };
    }, [
        disabled,
        tabRef,
        id
    ]);
    var _useCompoundItem = useCompoundItem(valueParam !== null && valueParam !== void 0 ? valueParam : tabValueGenerator, tabMetadata), value = _useCompoundItem.id, index = _useCompoundItem.index, totalTabsCount = _useCompoundItem.totalItemCount;
    var _useListItem = useListItem({
        item: value
    }), getTabProps = _useListItem.getRootProps, highlighted = _useListItem.highlighted, selected = _useListItem.selected;
    var _useButton = useButton({
        disabled: disabled,
        focusableWhenDisabled: !selectionFollowsFocus,
        type: 'button'
    }), getButtonProps = _useButton.getRootProps, buttonRefHandler = _useButton.rootRef, active = _useButton.active, focusVisible = _useButton.focusVisible, setFocusVisible = _useButton.setFocusVisible;
    var handleRef = useForkRef(tabRef, externalRef, buttonRefHandler);
    var tabPanelId = value !== undefined ? getTabPanelId(value) : undefined;
    var getRootProps = function() {
        var externalProps = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        var externalEventHandlers = extractEventHandlers(externalProps);
        var getCombinedRootProps = combineHooksSlotProps(getTabProps, getButtonProps);
        return _object_spread_props(_object_spread({}, externalProps, getCombinedRootProps(externalEventHandlers)), {
            role: 'tab',
            'aria-controls': tabPanelId,
            'aria-selected': selected,
            id: id,
            ref: handleRef
        });
    };
    return {
        getRootProps: getRootProps,
        active: active,
        focusVisible: focusVisible,
        highlighted: highlighted,
        index: index,
        rootRef: handleRef,
        // the `selected` state isn't set on the server (it relies on effects to be calculated),
        // so we fall back to checking the `value` prop with the selectedValue from the TabsContext
        selected: selected || value === selectedValue,
        setFocusVisible: setFocusVisible,
        totalTabsCount: totalTabsCount
    };
}
export { useTab };
